<template>
    <div class="cb-wizard-step2 cb-wizard__container">
        <div class="cb-wizard__body">
            <div class="cb-wizard__header">
                <h3 class="cb-wizard__title">Content Brief Wizard</h3>
                <Stepper class="cb-wizard__stepper"
                    :labels="['Step1', 'Step2', 'Step3', 'Step4', 'Step5']"
                    :currentStep="1" />
            </div>
            <div class="cb-wizard-step2__body">
                <div class="cb-wizard-step2__content">
                    <div v-if="keywordsList.length"
                        class="cb-wizard-step2__table-holder">
                        <div class="common-table cb-wizard-step2__table">
                            <div class="common-table__row common-table__header">
                                <div class="common-table__header-cell cb-wizard-step2__checkbox-cell">
                                    <base-check-box v-model="selectAllCbState"
                                        :trueValue="1"
                                        :falseValue="-1"
                                        :indeterminate="selectAllCbState === 0"
                                        @change="allKeywordsSelectionChange()" />
                                </div>

                                <div v-for="column in tableConfig"
                                    class="common-table__header-cell"
                                    :class="'cb-wizard-step2__' + column.propName + '-cell'">
                                    <base-table-sortable-column class="cb-wizard-step2__sortable-header"
                                        :label="column.label"
                                        :prop-name="column.propName"
                                        :sort-config="sortConfig"
                                        @click.native="onHeaderCellClicked(column)" />
                                </div>
                            </div>
                            <div class="cb-wizard-step2__table-body"
                                :ref="'scrollableTablePart'">
                                <div v-for="keywordItem in sortedKeywordsList"
                                    class="common-table__row"
                                    :class="{ 'cb-wizard-step2__row_selected': keywordItem.selected }">
                                    <div class="common-table__cell cb-wizard-step2__checkbox-cell">
                                        <base-check-box v-model="keywordItem.selected"
                                            @change="keywordSelectionChange(keywordItem)" />
                                    </div>
                                    <div v-for="column in tableConfig"
                                        class="common-table__cell"
                                        :class="'cb-wizard-step2__' + column.propName + '-cell'">
                                        <template v-if="column.propName === 'keyword'">
                                            {{ keywordItem[column.propName] }}
                                        </template>
                                        <template v-else>
                                            {{ keywordItem[column.propName] | commaDividedNumber }}
                                        </template>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <p><strong>{{ keywordsList.length }} Keywords Suggested:</strong> {{ totalSelectedKeywords
                            }} Selected</p>
                    </div>
                    <div v-else
                        class="cb-wizard-step2__not-found">
                        <p class="cb-wizard-step2__not-found-subtitle">0 Results based on starter keyword</p>
                        <p class="cb-wizard-step2__not-found-subtitle">Enter a new starter keyword and try again
                        </p>
                        <p>Try inputting another phrase and we’ll try and do better, we promise 🤟</p>
                        <ol>
                            <li>Aim for 3-5 word phrases for best suggestions - 2 is too few, more than five is too
                                jive for us often to
                                provide enough suggestions</li>
                            <li>A first round discovery will often lead to a second helpful query - doing a second
                                round with a second
                                starter keyword often leads to powerful second round suggestions!</li>
                            <li>Keyword volume is always based on history - if you have a ‘new new thing’ then past
                                data may not be helpful
                                for predicting future search behavior - follow your marketer’s instincts! </li>
                        </ol>

                    </div>
                </div>
            </div>
        </div>
        <div class="cb-wizard__sidebar">
            <span class="cb-wizard__sidebar-title">Discover Supporting Keywords</span>
            <p class="cb-wizard__sidebar-text">To support your Starter Keyword, it’s helpful to incorporate additional related keywords to your page concept mapping. This helps to understand the ways and different terminology your target audience may use related to this content topic.</p>
            <p class="cb-wizard__sidebar-text">You can have up to 25 keywords for planning this content brief, however it is not necessary to use all 25.</p>
            <span class="cb-wizard__sidebar-pro-tip">Pro tip: <base-material-design-icon icon="lightBulbOutline"/></span>
            <p class="cb-wizard__sidebar-text">Use a more specific keyword phrase as your starter keyword: 3-5+ word phrases help ensure more specific and useful keyword recommendations.</p>
            <div class="cb-wizard__controls">
                <button class="cb-wizard__button"
                    :disabled="!totalSelectedKeywords"
                    @click="next">Next</button>
                <button class="cb-wizard__button cb-wizard__button_inverted"
                    @click="back">Previous</button>
            </div>
        </div>
    </div>
</template>
<script>
import BaseSortMixins from '@/common/BaseSortMixins'
import filters from '@/common/Filters'
import helper from '@/services/Helper'
import Stepper from './stepper.vue'

const CB_STATES_MAP = {
    checked: 1,
    unchecked: -1,
    indeterminate: 0
}

export default {
    name: 'ContentBriefWizardStep2',
    mixins: [BaseSortMixins],
    filters: { ...filters },
    props: {
        keywordsList: Array
    },
    components: {
        Stepper
    },
    data() {
        return {
            tableConfig: [
                {
                    label: 'Keyword',
                    propName: 'keyword'
                },
                {
                    label: 'Search Volume / Mo.',
                    propName: 'msv'
                },
                {
                    label: 'Keyword Difficulty',
                    propName: 'difficultyScore'
                }
            ],
            selectAllCbState: CB_STATES_MAP.unchecked,
            sortConfig: [{
                sortDirection: helper.DIRECTION_DESC,
                propertyName: 'msv'
            }],
            totalSelectedKeywords: 0,
        }
    },
    computed: {
        sortedKeywordsList() {
            if (!this.sortConfig.length) {
                return this.keywordsList
            }

            function sortData(data, param, direction) {
                helper.sortByParamMutation(data, param, direction)
                data.forEach(item => {
                    if (item.children && item.children.length) {
                        sortData(item.children, param, direction)
                    }
                })
            }
            const { propertyName, sortDirection } = this.sortConfig[0]
            const sortedData = _.cloneDeep(this.keywordsList)
            sortData(sortedData, propertyName, sortDirection)

            return sortedData
        },
    },
    created() {
        this.totalSelectedKeywords = this.keywordsList.filter(keyword => !!keyword.selected).length;
        this.setSelectAllCbState();
    },
    methods: {
        allKeywordsSelectionChange() {
            this.keywordsList.forEach((keyword, index) => {
                const isSelected = this.selectAllCbState === CB_STATES_MAP.checked
                keyword.selected = isSelected
                this.sortedKeywordsList[index].selected = isSelected
            });
            this.totalSelectedKeywords = this.selectAllCbState === CB_STATES_MAP.checked ? this.keywordsList.length : 0;
            this.setSelectAllCbState();
        },
        keywordSelectionChange(keyword) {
            keyword.selected ? this.totalSelectedKeywords++ : this.totalSelectedKeywords--
            // due to sorting is copying original list, need to update item in original list. Else sorting will drop selection
            const originalKeywordtoChange = this.keywordsList.find(originalKeyword => keyword.keyword === originalKeyword.keyword)
            if (!originalKeywordtoChange) {
                return
            }
            originalKeywordtoChange.selected = keyword.selected;
            this.setSelectAllCbState();
            
        },
        setSelectAllCbState() {
            if (this.totalSelectedKeywords <= 0) {
                this.selectAllCbState = CB_STATES_MAP.unchecked
            } else if (this.totalSelectedKeywords >= this.keywordsList.length) {
                this.selectAllCbState = CB_STATES_MAP.checked
            } else {
                this.selectAllCbState = CB_STATES_MAP.indeterminate
            }
        },
        next() {
            if (!this.totalSelectedKeywords) {
                return;
            }
            const selectedKeywords = this.sortedKeywordsList.filter(keyword => keyword.selected);
            this.$emit('keywordsSelected', selectedKeywords);
        },
        back() {
            this.$emit('previous');
        }
    },
    mounted() {

    }
}
</script>

<style scoped
    lang="scss">
    .cb-wizard-step2 {
        max-height: 95vh;
        box-sizing: border-box;

        &__table {
            display: flex;
            flex-direction: column;
            max-height: calc(95vh - 310px);
        }

        &__table-body {
            flex: 0 1 100%;
            overflow-y: auto;
        }

        &__sortable-header {
            background: transparent;
            color: $rfx-dark-march;
            font-size: 14px;
            font-weight: 700;
        }

        .common-table {
            border: 1px solid $rfx-light-steel;

            &__header {
                background-color: rgba(1, 22, 39, .1);
                padding-right: 16px;
            }

            &__header-cell,
            &__cell {
                padding-top: 10px;
                padding-bottom: 10px;
            }
        }

        &__checkbox-cell {
            flex: 0 0 20px;
            padding-left: 20px;
            padding-right: 0;
        }

        &__keyword-cell {
            flex: 2 0 180px;
            padding-left: 20px;
            padding-right: 0;
        }

        &__msv-cell {
            flex: 2 0 150px;
        }

        &__difficultyScore-cell {
            flex: 1 0 120px;
        }

        &__row_selected {
            background-color: $rfx-almost-white;
        }

        &__tutorial-item {
            display: flex;
            gap: 10px;
            margin: 0 0 15px;

            p {
                margin: 0;
            }
        }

        &__tutorial-icon {
            font-size: 18px;
            color: $rfx-bright-blue;
        }

        &__not-found {
            p {
                margin: 0 0 15px;
            }

            ol {
                margin: 0;
                padding-left: 20px;
            }

            li {
                margin-bottom: 15px;
            }
        }

        &__not-found-subtitle {
            font-weight: 500;
        }
    }
</style>